import { DateTime } from 'luxon';

/** Matches MAX_SCHEDULE_HORIZON_DAYS on the server, which rejects anything further out. */
export const MAX_SCHEDULE_HORIZON_DAYS = 90;

/** Minimum lead time, so the picked slot hasn't already passed by the time the sweep runs. */
const MIN_LEAD_MINUTES = 5;

export interface ScheduledFor {
  /** Absolute instant, resolved from the picked wall-clock time in the browser's zone. */
  scheduledFor: string;
  /** The zone it was resolved in, so the portal can redisplay it as the rescue picked it. */
  scheduledForTimezone: string;
}

/**
 * The browser's own zone. There is no rescue- or user-level timezone to read, so the wall-clock time
 * the rescue types is interpreted here and the zone is sent along with the instant — otherwise
 * "9am Tuesday" would silently mean 9am UTC for staff who never set a zone.
 */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Turns the two native inputs (`YYYY-MM-DD` + `HH:mm`) into an instant plus its zone. */
export function combineDateTime(date: string, time: string): ScheduledFor | null {
  if (!date || !time) {
    return null;
  }
  const timezone = browserTimezone();
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: timezone });
  if (!dt.isValid) {
    return null;
  }
  return { scheduledFor: dt.toUTC().toISO()!, scheduledForTimezone: timezone };
}

/** The reason a picked date/time can't be used, or null when it's fine. */
export function scheduleValidationError(date: string, time: string): string | null {
  if (!date || !time) {
    return null;
  }
  const combined = combineDateTime(date, time);
  if (!combined) {
    return 'That date and time isn’t valid.';
  }
  const picked = DateTime.fromISO(combined.scheduledFor);
  const minutesOut = picked.diffNow('minutes').minutes;
  if (minutesOut < MIN_LEAD_MINUTES) {
    return `Pick a time at least ${MIN_LEAD_MINUTES} minutes from now.`;
  }
  if (picked.diffNow('days').days > MAX_SCHEDULE_HORIZON_DAYS) {
    return `Hand-offs can only be scheduled up to ${MAX_SCHEDULE_HORIZON_DAYS} days ahead.`;
  }
  return null;
}

/** `YYYY-MM-DD` for today and for the last bookable day, for the date input's min/max. */
export function dateInputBounds() {
  const now = DateTime.local();
  return {
    min: now.toFormat('yyyy-MM-dd'),
    max: now.plus({ days: MAX_SCHEDULE_HORIZON_DAYS }).toFormat('yyyy-MM-dd'),
  };
}

/**
 * Splits a booked instant back into the two native input values, read in the zone it was picked in
 * so the fields show the wall-clock time the rescue originally chose.
 */
export function splitDateTime(iso: string, timezone?: string | null) {
  const dt = DateTime.fromISO(iso, { zone: timezone || browserTimezone() });
  if (!dt.isValid) {
    return { date: '', time: '' };
  }
  return { date: dt.toFormat('yyyy-MM-dd'), time: dt.toFormat('HH:mm') };
}

/**
 * Renders a booked instant back in the zone it was picked in, so a rescue that booked "9:00 AM" sees
 * 9:00 AM even if the browser is somewhere else now.
 */
export function formatScheduledFor(iso: string | null | undefined, timezone?: string | null): string {
  if (!iso) {
    return '—';
  }
  const dt = DateTime.fromISO(iso, { zone: timezone || browserTimezone() });
  if (!dt.isValid) {
    return '—';
  }
  return dt.toFormat('LLL d, yyyy • h:mm a ZZZZ');
}
